import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import classNames from 'classnames';
import css from './AdvancedAutoComplete.css';
import { AutoCompleteSizes, SizesToTagsLimit } from './consts';
import { useOutsideEventHandler } from '../../../utils/customHooks';

import { AutoCompleteOptionType } from '../autocompleteTypes';
import AdvancedAutoCompleteInput from './AdvancedAutoCompleteInput';
import AdvancedAutoCompleteDropdown from './AdvancedAutoCompleteDropdown';

const MOUSE_ONLY_EVENTS = [{ name: 'mousedown' }];

const AdvancedAutoComplete = ({
    options = [],
    values = [],
    onChange,
    loading = false,
    label,
    disabled = false,
    groupBy,
    className,
    size = AutoCompleteSizes.Medium,
    removable = false,
    shouldWarnEmpty = false,
    onRemove,
    defaultOpen = false,
    lazy = false,
    isMulti = false,
    clearable = false,
    renderOption,
    disableOptions = false,
    openUpward = false,
    dataTestId = null,
    ignoreScrollEvents = false,
    selectableGroupHeaders = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');

    const dropdownRef = useRef();
    const [lazyValues, setLazyValues] = useState([]);

    const tagsLimit = SizesToTagsLimit[size];
    const useLargeTagWidth = size === AutoCompleteSizes.XLarge;

    const handleClickOutside = useCallback(() => {
        if (isOpen) setIsOpen(false);
    }, [isOpen]);

    useOutsideEventHandler(dropdownRef, handleClickOutside, ignoreScrollEvents ? MOUSE_ONLY_EVENTS : undefined);

    useEffect(() => {
        if (defaultOpen)
            setTimeout(() => {
                setIsOpen(true);
            }, 0);
    }, [defaultOpen]);

    useEffect(() => {
        if (isOpen) {
            if (lazy && !lazyValues.length) setLazyValues(values);
            return;
        }

        if (search) setSearch('');

        if (lazy && (lazyValues.length || values.length)) onChange(lazyValues);
    }, [isOpen]);

    const filteredOptions = useMemo(
        () => (!search ? options : options.filter(item => item.label.toLowerCase().includes(search.toLowerCase()))),
        [search, options]
    );

    const handleFieldClick = useCallback(event => {
        setIsOpen(prev => !prev);
        event.preventDefault();
    }, []);

    const onChangeSingle = value => {
        setIsOpen(false);
        onChange(value);
    };

    const valuesProps =
        lazy && isOpen
            ? { values: lazyValues, onChange: setLazyValues }
            : { values, onChange: isMulti ? onChange : onChangeSingle };

    const isEmptyWarning = shouldWarnEmpty && !isOpen && !removable && !values.length;

    return (
        <div className={classNames(css.container, className, css[size])} data-testid={dataTestId}>
            <div className={css.wrapper} ref={dropdownRef}>
                <span
                    className={classNames(css.label, {
                        [css.labelFloat]: isOpen || values.length,
                        [css.placeholder]: !isOpen && !values.length,
                    })}
                >
                    <Translate id={label} />
                </span>
                <AdvancedAutoCompleteInput
                    isOpen={isOpen}
                    loading={loading}
                    removable={removable}
                    onRemove={onRemove}
                    onFieldClick={handleFieldClick}
                    search={search}
                    setSearch={setSearch}
                    tagsLimit={tagsLimit}
                    useLargeTagWidth={useLargeTagWidth}
                    disabled={disabled}
                    isMulti={isMulti}
                    isEmptyWarning={isEmptyWarning}
                    clearable={clearable}
                    {...valuesProps}
                />
                {isOpen && (
                    <AdvancedAutoCompleteDropdown
                        groupBy={groupBy}
                        filteredOptions={filteredOptions}
                        disabled={disabled || loading || disableOptions}
                        lazy={lazy}
                        {...valuesProps}
                        isMulti={isMulti}
                        onChangeSingle={onChangeSingle}
                        renderOption={renderOption}
                        openUpward={openUpward}
                        selectableGroupHeaders={selectableGroupHeaders}
                    />
                )}
            </div>
            {isEmptyWarning && (
                <span className={css.emptyWarning}>
                    <Translate id="STATIC.AUTOCOMPLETE.MANDATORY_FIELD" />
                </span>
            )}
        </div>
    );
};

AdvancedAutoComplete.propTypes = {
    options: PropTypes.arrayOf(AutoCompleteOptionType),
    values: PropTypes.arrayOf(AutoCompleteOptionType),
    onChange: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    groupBy: PropTypes.func,
    className: PropTypes.string,
    size: PropTypes.oneOf(Object.values(AutoCompleteSizes)),
    removable: PropTypes.bool,
    onRemove: PropTypes.func,
    defaultOpen: PropTypes.bool,
    lazy: PropTypes.bool,
    isMulti: PropTypes.bool,
    shouldWarnEmpty: PropTypes.bool,
    clearable: PropTypes.bool,
    renderOption: PropTypes.func,
    disableOptions: PropTypes.bool,
    openUpward: PropTypes.bool,
    dataTestId: PropTypes.string,
    ignoreScrollEvents: PropTypes.bool,
    selectableGroupHeaders: PropTypes.bool,
};

export default AdvancedAutoComplete;
